import { useEffect, useRef, useState } from 'react'
import ReactQuill from 'react-quill-new'
import 'quill/dist/quill.snow.css'
import { cn } from '@neo/lib/utils'

import { ScrollArea } from '@neo/primitives/scroll-area'
import { type Node, type NodeProps, NodeResizeControl, useReactFlow } from '@xyflow/react'
import { useFlowStore } from '../../store'
import type { NoteNodeData } from '../../types'
import { backgroundColorOptions, formats, modules } from './note.constants'
import { NoteToolbar } from './note-toolbar'
// Custom styles for Quill editor
import './quill-editor.css'

const NOTE_MIN_WIDTH = 350
const NOTE_MIN_HEIGHT = 200

export const NoteNode = ({ id, data }: NodeProps<Node<NoteNodeData>>) => {
  const content = data?.config.content
  const color = data?.config.color || 'blue'
  const currentBackgroundColor =
    backgroundColorOptions[color as keyof typeof backgroundColorOptions]?.bg
  const borderColor = backgroundColorOptions[color as keyof typeof backgroundColorOptions]?.border
  const handleTopColor =
    backgroundColorOptions[color as keyof typeof backgroundColorOptions]?.handleTop
  const flowInstance = useReactFlow()

  const setContent = (newContent: string) => {
    if (flowInstance) {
      flowInstance.updateNodeData(id, { config: { ...data.config, content: newContent } })
    }
  }
  const [textSize, setTextSize] = useState('normal')
  const quillRef = useRef<ReactQuill>(null)
  const isEditing = useFlowStore((state) => state.isEditing)

  const handleChange = (newContent: string) => {
    setContent(newContent)
  }

  const handleBackgroundColorChange = (newBgColor: string) => {
    if (flowInstance) {
      flowInstance.updateNodeData(id, { config: { ...data.config, color: newBgColor } })
    }
  }

  const handleSizeChange = (value: string | string[]) => {
    const sizeValue = typeof value === 'string' ? value : ''
    setTextSize(sizeValue)
    const quillSizeValue = sizeValue === 'normal' ? '' : sizeValue
    if (quillRef.current) {
      const editor = quillRef.current.getEditor()
      editor.format('size', quillSizeValue)
    }
  }

  const handleInsertLink = (url: string) => {
    if (quillRef.current) {
      const editor = quillRef.current.getEditor()
      const range = editor.getSelection()
      if (range) {
        if (url) editor.format('link', url)
      } else {
        // If no text is selected, insert the link at the cursor position.
        // Alternatively, you might want to insert the URL as plain text
        // or provide feedback to the user to select text first.
        editor.focus()
        // Check if the editor has a method to insert text at the current cursor position
        // For ReactQuill, this might involve inserting the URL text and then formatting it as a link
        // or directly using formatText if available and appropriate for links.
        // For simplicity, we'll ensure it has focus. The toolbar handles empty URL.
        if (url) editor.format('link', url) // This will make the currently typed text (if any) a link or insert a link if selection is collapsed
      }
    }
  }

  const selectNode = useFlowStore((state) => state.selectNode)
  useEffect(() => {
    if (quillRef.current) {
      const editor = quillRef.current.getEditor()
      editor.enable(true)
      editor.focus()
    }
  }, [quillRef, id, selectNode])

  useEffect(() => {
    if (quillRef.current) {
      const editor = quillRef.current.getEditor()
      editor.enable(isEditing)
    }
  }, [isEditing])

  return (
    <div
      className={cn(
        `relative min-w-[${NOTE_MIN_WIDTH}px] min-h-auto border rounded-md h-full w-full cursor-move`,
        currentBackgroundColor, // Apply dynamic background color
        borderColor,
      )}
      data-testid="note-node"
      onClick={() => selectNode(id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') selectNode(id)
      }}
      tabIndex={0}
      role="button"
    >
      <div className={cn('w-full h-2 cursor-move', handleTopColor)} />
      <div
        className={cn(
          `${backgroundColorOptions[color as keyof typeof backgroundColorOptions]?.text}`,
        )}
      >
        <NodeResizeControl
          minWidth={NOTE_MIN_WIDTH}
          minHeight={NOTE_MIN_HEIGHT}
          position="bottom-right"
          className={cn('note-resize-handle', `!${currentBackgroundColor}`)}
          aria-label="Resize note node"
        />
      </div>
      <div
        className="flex flex-col rounded-md w-full"
        style={{ position: 'absolute', top: 6, left: 0, right: 0, bottom: 12 }}
      >
        <NoteToolbar
          nodeId={id}
          quillRef={quillRef}
          isEditing={isEditing}
          currentBackgroundColor={currentBackgroundColor}
          textSize={textSize}
          onBackgroundColorChange={handleBackgroundColorChange}
          onSizeChange={handleSizeChange}
          onInsertLink={handleInsertLink}
        />

        <ScrollArea
          className="flex-1 pointer-events-auto w-full h-[calc(100%-0px)]"
          data-testid="note-editor-wrapper"
          role="textbox"
          tabIndex={0}
          aria-label="Note editor"
        >
          <ReactQuill
            ref={quillRef}
            theme="snow"
            value={content}
            onChange={handleChange}
            modules={modules}
            formats={formats}
            placeholder="Add your notes here..."
            className="nodrag nopan nowheel note-quill-editor border-none !h-auto text-foreground cursor-text"
            style={{ height: '100%' }}
          />
        </ScrollArea>
      </div>
    </div>
  )
}
